import { action, observable, makeObservable } from 'mobx';

import { apiCaller } from '@services/apiCaller';
import { IRootStore } from '@stores/index';
import { Group } from '@components/common/formSchema';

import { SourceDefinition } from './sourceDefinition';
import { DefinitionHiddenOption, SourceDefinitionCategory } from './types';

type SourceType =
  | 'cloud'
  | 'cloudSource'
  | 'warehouse'
  | 'web'
  | 'android'
  | 'android_kotlin'
  | 'ios'
  | 'ios_swift'
  | 'unity'
  | 'reactnative'
  | 'amp'
  | 'flutter'
  | 'cordova';
export interface ISourceDefinitionsListStore {
  sourceDefinitions: ISourceDefinition[];
  rootStore: IRootStore;
  getAll(): Promise<void>;
  getById(id: string): ISourceDefinition | undefined;
  getSourceDefinitionModelById(id: string): SourceDefinition | undefined;
  getByCategory(category: SourceDefinitionCategory): ISourceDefinition[];
  getByName(name: string): ISourceDefinition | undefined;
}

type WarehouseSourceOptions = {
  isSqlModelSupported?: boolean;
  syncBehaviours: string[];
  isCredentialsValidationSupported?: boolean;
  isAudienceSupported?: boolean;
  isDataGraphSupported?: boolean;
  syncReportType?: string;
  supportsSyncSettings?: boolean;
};

export type AuthOptions = {
  provider?: 'Google' | 'Xero';
  oauthRole?: string;
};

const popularSources = [
  'Javascript',
  'HTTP',
  'android_kotlin',
  'Node',
  'ios_swift',
  'Python',
  'AMP',
  'snowflake',
  'google_sheets',
  'webhook',
];

const newSources = ['android_kotlin', 'ios_swift'];

interface Field {
  trim?: boolean;
  type?: string;
  label?: string;
  regex?: string;
  value?: string;
  required?: boolean;
  regexErrorMessage?: string;
  addInAccountSummary?: boolean;
  placeholder?: string;
}

export interface UiConfig {
  auth?: {
    type: string;
    secretFields?: string[];
    nameField: string;
    config?: Group[];
    role?: string;
  };
  title?: string;
  sectionNote?: string;
  fields?: Array<Field>;
  docLinks?: {
    jsonMapperUseInstructions?: string;
    setupInstructions?: string;
    grantPermissions?: string;
    verifyingCredentials?: string;
  };
  config?: Group[];
  schemaAlias?: string;
  secretFields?: string[];
  nameField?: string;
  /** A creation flow of the type's own, e.g. 'managed-webhook', which the directory's setup follows. */
  createFlow?: string;
}

export interface ISourceDefinition {
  id: string;
  name: string;
  category: SourceDefinitionCategory;
  displayName: string;
  config?: {
    supportedAccountDefinitions?: {
      rudderAccountId: string[];
    };
  };
  options:
    | ({
        isBeta: boolean;
        isNew: boolean;
        legacy?: {
          notice?: string;
          selectionNotice?: string;
          migrationGuide?: string;
        };
        image: string;
        hidden?: DefinitionHiddenOption;
        deprecated?: boolean;
        deprecationLabel?: string;
        botEventManagement?: boolean;
        supportsDestinationSyncMode?: boolean;
        auth?: AuthOptions;
        gaMetricIds?: string[];
        gaDimensionIds?: string[];
        sdkExecutionEnvironment?: string;
        isMobilePlatform?: boolean;
      } & WarehouseSourceOptions)
    | null;
  isPopular: boolean;
  uiConfig: UiConfig[] | UiConfig | null;
  type: SourceType;
}

export class SourceDefinitionsListStore implements ISourceDefinitionsListStore {
  @observable public sourceDefinitions: ISourceDefinition[] = [];

  @observable public rootStore: IRootStore;

  @observable private sourceDefinitionModels: SourceDefinition[] = [];

  constructor(rootStore: IRootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @action.bound
  public async getAll() {
    const res =
      await apiCaller().get<Omit<ISourceDefinition, 'isPopular'>[]>('/web/source-definitions');
    const modifiedSourceDefs = res.data.map((x) => ({
      id: x.id,
      name: x.name,
      displayName: x.displayName,
      category: x.category,
      uiConfig: x.uiConfig,
      options: x.options,
      config: x.config,
      type: x.type,
    }));

    this.sourceDefinitions = modifiedSourceDefs
      .filter((x) => !!x.displayName)
      .sort((a, b) => {
        const aIsNew = newSources.includes(a.name);
        const bIsNew = newSources.includes(b.name);

        // Promote new sources ahead
        if (aIsNew && !bIsNew) {
          return -1;
        }
        if (!aIsNew && bIsNew) {
          return 1;
        }

        // Otherwise sort alphabetically
        return a.displayName.toLowerCase() > b.displayName.toLowerCase() ? 1 : -1;
      })
      .map((a) => ({ ...a, isPopular: popularSources.includes(a.name) }));

    this.sourceDefinitionModels = res.data.map((s) => new SourceDefinition(s));
  }

  @action.bound
  public getById(id: string) {
    return this.sourceDefinitions.find((def: ISourceDefinition) => def.id === id);
  }

  @action.bound
  public getSourceDefinitionModelById(id: string) {
    return this.sourceDefinitionModels.find((def: SourceDefinition) => def.id === id);
  }

  public getByCategory(category: SourceDefinitionCategory) {
    return this.sourceDefinitions.filter((def) => def.category === category);
  }

  public getByName(name: string) {
    return this.sourceDefinitions.find((def) => def.name === name);
  }
}
